/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import {
  AssetTypes,
  getAssetRatio,
} from '../../../../../hooks/studioContext/CMSParams';
import { createDynamicCropAspectRatio } from './dynamicCrop';

const ASPECT_RATIO_BY_ASSET_TYPE = {
  [AssetTypes.Story]: '9:16',
  [AssetTypes.Clip]: '9:16',
  [AssetTypes.Ad]: '9:16',
  [AssetTypes.Vertical]: '9:16',
  [AssetTypes.Landscape]: '16:9',
  [AssetTypes.Portrait]: '4:5',
  [AssetTypes.Print]: '2:3',
  [AssetTypes.StoryThumbnail]: '2:3',
  [AssetTypes.ClipThumbnail]: '2:3',
  [AssetTypes.ThreeFour]: '3:4',
  [AssetTypes.Square]: '1:1',
  [AssetTypes.StoryIcon]: '1:1',
  [AssetTypes.CardsAsset]: '1:1',
  [AssetTypes.Wide]: '4:1',
};

const isFinitePositiveNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

function getRatioAssetType(studioContext) {
  const assetType = studioContext?.assetType;

  if (assetType === AssetTypes.CardsAsset) {
    return studioContext?.cardsAssetType || AssetTypes.CardsAsset;
  }

  if (assetType === AssetTypes.ContentBankAsset) {
    return studioContext?.contentBankAssetType;
  }

  return assetType;
}

export function getDynamicCropTargetAspectRatio({
  studioContext,
  fallbackWidth,
  fallbackHeight,
} = {}) {
  const ratioAssetType = getRatioAssetType(studioContext);
  const configuredAspectRatio = ASPECT_RATIO_BY_ASSET_TYPE[ratioAssetType];

  if (configuredAspectRatio) {
    return configuredAspectRatio;
  }

  const ratio = getAssetRatio(ratioAssetType);
  if (isFinitePositiveNumber(ratio)) {
    return createDynamicCropAspectRatio(ratio, 1);
  }

  return createDynamicCropAspectRatio(fallbackWidth, fallbackHeight);
}
